import { Link } from 'react-router-dom';
import type { SignInFlowError } from '../../hooks/classify-auth-error';

/**
 * サインアップ・パスワード再設定で、新しいパスワードが漏洩パスワードとして阻止されたときの文言（要件 6.3）
 */
export const COMPROMISED_PASSWORD_MESSAGE =
  'このパスワードは、外部で漏洩が確認されているため使用できません。別のパスワードを指定してください。';

/**
 * 既存アカウントで再サインアップしたとき、登録済みのパスワードが漏洩パスワードとして阻止された場合の文言
 */
export const EXISTING_ACCOUNT_COMPROMISED_MESSAGE =
  'このメールアドレスは既に登録されています。登録済みのパスワードは外部で漏洩が確認されているため、パスワードを再設定してください（サインイン画面でこのパスワードを入力すると、再設定への案内が表示されます）。';

/** アカウント作成画面（デスクトップ向け） */
export const DEFAULT_SIGN_UP_HREF = '/auth/signup';

/** パスワード再設定画面（デスクトップ向け・Web 向けで共通） */
export const DEFAULT_RESET_PASSWORD_HREF = '/auth/forgot-password';

interface AuthErrorMessageProps {
  error: SignInFlowError | null;
  /** パスワード再設定への導線の遷移先 */
  resetPasswordHref?: string;
  /** アカウント作成への導線の遷移先（確認コード未入力のアカウント向け） */
  signUpHref?: string;
}

/**
 * 認証エラーの分類ごとの固定文言。
 *
 * 例外の生メッセージや内部のステップ名は表示しない。資格情報の誤りには必ずパスワード再設定への
 * 導線を付ける（漏洩パスワードによる阻止を判別し損ねても、利用者が再設定にたどり着けるように）。
 */
const MESSAGES: Record<SignInFlowError['kind'], string> = {
  invalidCredentials: 'メールアドレスまたはパスワードが正しくありません。',
  codeMismatch:
    'コードが正しくありません。認証アプリに表示されている最新のコードを入力してください。',
  sessionExpired:
    '時間切れになりました。お手数ですが、メールアドレスとパスワードの入力からやり直してください。',
  tooManyAttempts: '試行回数が上限に達しました。しばらく時間をおいてから、もう一度お試しください。',
  compromisedCredentials:
    'このパスワードは、外部で漏洩が確認されているため使用できません。パスワードを再設定してください。',
  blockedForRisk: '安全のため、このサインインは阻止されました。管理者にお問い合わせください。',
  signUpNotConfirmed:
    'アカウントの確認が完了していません。アカウント作成の画面で同じメールアドレスとパスワードを入力すると、確認コードが再送されます。',
  unsupported:
    'サインインを完了できませんでした。時間をおいてもう一度お試しください。解決しない場合は、利用者向けガイドの「よくある質問」をご確認ください。',
  unknown:
    'サインインを完了できませんでした。時間をおいてもう一度お試しください。解決しない場合は、利用者向けガイドの「よくある質問」をご確認ください。',
};

const WITH_RESET_LINK = new Set<SignInFlowError['kind']>([
  'invalidCredentials',
  'compromisedCredentials',
]);

export function AuthErrorMessage({
  error,
  resetPasswordHref = DEFAULT_RESET_PASSWORD_HREF,
  signUpHref = DEFAULT_SIGN_UP_HREF,
}: AuthErrorMessageProps) {
  if (!error) {
    return null;
  }
  return (
    <div role="alert" className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 space-y-1">
      <p className="text-red-400 text-sm">{MESSAGES[error.kind]}</p>
      {WITH_RESET_LINK.has(error.kind) && (
        <Link
          to={resetPasswordHref}
          className="inline-block text-sm text-primary-400 hover:text-primary-300 underline"
        >
          パスワードを再設定する
        </Link>
      )}
      {error.kind === 'signUpNotConfirmed' && (
        <Link
          to={signUpHref}
          className="inline-block text-sm text-primary-400 hover:text-primary-300 underline"
        >
          アカウント作成へ進む
        </Link>
      )}
    </div>
  );
}
